import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Clock, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { ticketsApi } from '../api/tickets';
import { tasksApi } from '../api/tasks';
import { dashboardApi, type DashboardWidget } from '../api/dashboard';
import { useTicketStatuses } from '../hooks/useTicketStatuses';
import DashboardGrid from '../components/dashboard/DashboardGrid';
import { DEFAULT_LAYOUT } from '../components/dashboard/widgets';
import { STAFF_TILES, type NavTile } from '../lib/nav';

const GROUPS: NavTile['group'][] = ['Work', 'CRM', 'Account'];

export default function Home() {
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const queryClient = useQueryClient();

  // Feeds the StatusBadge registry so widgets show custom labels/colors.
  useTicketStatuses();

  const { data: stats } = useQuery({
    queryKey: ['ticket-stats'],
    queryFn: () => ticketsApi.getStats(),
    enabled: !!currentCompany,
  });
  const slaBreached = (stats as any)?.slaBreached ?? 0;

  // Tickets parked on you (waiting-on-customer, and you're the creator /
  // contact / reporter / CC). Keyed under 'tickets' so every ticket
  // invalidation refreshes it — a count here that lags is worse than none.
  const { data: waitingOnMe } = useQuery({
    queryKey: ['tickets', 'waiting-on-me'],
    queryFn: () => ticketsApi.list({ waitingOnMe: '1', limit: 1 }),
    enabled: !!currentCompany,
  });
  const waitingCount = waitingOnMe?.total ?? 0;

  // Sub-ticket tasks assigned to you — "it's your work now". Under 'tasks' so
  // every task change (socket) refreshes it.
  const { data: myTasks } = useQuery({
    queryKey: ['tasks', 'mine-count'],
    queryFn: () => tasksApi.mine('open'),
    enabled: !!currentCompany,
  });
  const taskCount = myTasks?.openCount ?? 0;

  // Saved layout. null = never customised, so show the default — which stays
  // editable without writing a row on first view.
  const { data: saved, isLoading: layoutLoading } = useQuery({
    queryKey: ['dashboard-layout', (currentCompany as any)?.id],
    queryFn: () => dashboardApi.getLayout(),
    enabled: !!currentCompany,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DashboardWidget[] | null>(null);

  // Seed the draft from whichever layout is in force when editing starts.
  // Keyed on the company id, not the fetched object, so a refetch mid-edit
  // can't overwrite what the user is arranging.
  const live = (saved ?? DEFAULT_LAYOUT) as DashboardWidget[];
  useEffect(() => {
    setEditing(false);
    setDraft(null);
  }, [(currentCompany as any)?.id]);

  const saveMutation = useMutation({
    mutationFn: (widgets: DashboardWidget[]) => dashboardApi.saveLayout(widgets),
    onSuccess: (widgets) => {
      queryClient.setQueryData(['dashboard-layout', (currentCompany as any)?.id], widgets);
      queryClient.invalidateQueries({ queryKey: ['dashboard-layout'] });
      setEditing(false);
      setDraft(null);
      toast.success('Dashboard saved');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetMutation = useMutation({
    mutationFn: () => dashboardApi.resetLayout(),
    onSuccess: () => {
      queryClient.setQueryData(['dashboard-layout', (currentCompany as any)?.id], null);
      queryClient.invalidateQueries({ queryKey: ['dashboard-layout'] });
      setEditing(false);
      setDraft(null);
      toast.success('Dashboard reset to the default');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const tiles = STAFF_TILES.filter((t) => !t.superOnly || user?.isSuperAdmin);
  const shown = editing ? (draft ?? live) : live;

  return (
    <div className="flex flex-col gap-5 pb-10">
      {/* Heading */}
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h1 className="page-title">Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
        <div className="flex items-center gap-4 flex-wrap">
          {currentCompany && taskCount > 0 && (
            <Link
              to="/my-work"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold"
              style={{ color: 'var(--scpa-primary)' }}
              title="Sub-ticket tasks assigned to you that are still open"
            >
              <ClipboardCheck className="w-4 h-4" /> {taskCount} task{taskCount === 1 ? '' : 's'} for you
            </Link>
          )}
          {currentCompany && waitingCount > 0 && (
            <Link
              to="/tickets?waitingOnMe=1"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold"
              style={{ color: 'var(--scpa-status-warning)' }}
              title="Tickets in a waiting status where the team needs a reply, test, or confirmation from you"
            >
              <Clock className="w-4 h-4" /> {waitingCount} ticket{waitingCount === 1 ? '' : 's'} waiting on you
            </Link>
          )}
          {currentCompany && slaBreached > 0 && (
            <Link
              to="/tickets"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold"
              style={{ color: 'var(--scpa-status-warning)' }}
            >
              <AlertTriangle className="w-4 h-4" /> {slaBreached} SLA breach{slaBreached === 1 ? '' : 'es'}
            </Link>
          )}
        </div>
      </div>

      {/* Compact nav bar — the old hub tiles collapsed into a menu strip */}
      <nav className="flex flex-wrap items-center gap-x-1 gap-y-2">
        {GROUPS.map((group, gi) => {
          const groupTiles = tiles.filter((t) => t.group === group);
          if (!groupTiles.length) return null;
          return (
            <div key={group} className="flex items-center gap-1">
              {gi > 0 && <span className="mx-1.5 h-4 w-px" style={{ background: 'var(--scpa-border)' }} />}
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted pr-1">{group}</span>
              {groupTiles.map(({ to, label, icon: Icon }) => (
                <Link key={to} to={to} className="scpa-ghost-btn" title={label}>
                  <Icon className="w-3.5 h-3.5" /> <span>{label}</span>
                </Link>
              ))}
            </div>
          );
        })}
      </nav>

      {!currentCompany ? (
        <div className="card p-8 text-center text-muted">
          <p>You need to be part of a company to manage tickets.</p>
          <p className="text-sm mt-2">Contact your administrator, or join one from the customer portal.</p>
        </div>
      ) : layoutLoading ? (
        <div className="py-16 text-center text-muted text-sm">Loading your dashboard…</div>
      ) : (
        <DashboardGrid
          widgets={shown}
          editing={editing}
          saving={saveMutation.isPending || resetMutation.isPending}
          onEdit={() => { setDraft(live); setEditing(true); }}
          onChange={setDraft}
          onSave={() => saveMutation.mutate(draft ?? live)}
          onCancel={() => { setEditing(false); setDraft(null); }}
          onReset={() => resetMutation.mutate()}
        />
      )}
    </div>
  );
}
